// slashItems — the `/` menu's projection of the per-template EditorShape.
//
// Promoted from sketches/editor/slashItems.ts (document-first-editor.md §2.2).
// The one idea it makes tangible: the slash menu is NOT a fixed Notion list —
// it is the SAME `EditorShape` the Palette already reads, re-projected into a
// keyboard popup. Feed it the quiz template's Shape and only question-shaped
// rows appear; feed it the rich Shape and every block + media kind shows. There
// is no per-template `if` here — the allow-list IS the menu.
//
// Discipline mirrors Palette.tsx: kind → author-facing metadata is a TOTAL
// switch on the discriminant (no cast; a new kind stops it compiling until
// named), and the pure switch returns `msg` MessageDescriptors, localised at
// the impure edge (`resolveSlashItems`), never a hand-authored string.

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import type { IconName } from "../../Icon/Icon.js";
import type {
    EditorShape,
    InsertIntent,
    MediaKind,
} from "../Palette/Palette.js";
import type { ElementKind } from "../types.js";

// The author-facing shape of one slash row, already localised — what the menu
// renders and reports up. `keywords` are extra filter tokens ("h1", "question")
// so `/quest` can find the MCQ row even though its label is "Multiple-choice
// question"; matching runs over label + keywords (matchSlash below).
export type SlashItem = {
    readonly intent: InsertIntent;
    readonly label: string;
    readonly hint: string;
    readonly icon: IconName;
    readonly keywords: readonly string[];
};

// The pre-localisation spec: descriptors + icon + keywords, no i18n bound yet.
// A projection of the Shape, in the Shape's own order — the single source.
type SlashSpec = {
    readonly intent: InsertIntent;
    readonly label: MessageDescriptor;
    readonly hint: MessageDescriptor;
    readonly icon: IconName;
    readonly keywords: readonly string[];
};

// kind → metadata, total over ElementKind. Same words as Palette's blockMeta;
// keywords add the shortcuts an author types ("h1", "para", "quiz").
const blockSpec = (kind: ElementKind): Omit<SlashSpec, "intent"> => {
    switch (kind) {
        case "heading":
            return {
                label: msg`Heading`,
                hint: msg`A section title`,
                icon: "heading",
                keywords: ["h1", "h2", "h3", "title", "section"],
            };
        case "prose":
            return {
                label: msg`Paragraph`,
                hint: msg`A block of body text`,
                icon: "paragraph",
                keywords: ["text", "para", "body"],
            };
        case "objective":
            return {
                label: msg`Learning objective`,
                hint: msg`What the learner should be able to do`,
                icon: "objective",
                keywords: ["goal", "outcome", "list"],
            };
        case "mcq":
            return {
                label: msg`Multiple-choice question`,
                hint: msg`A question with selectable answers`,
                icon: "mcq",
                keywords: ["question", "quiz", "mcq", "answer"],
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// kind → metadata, total over MediaKind. The Shape gates these too, so a quiz
// Shape with an empty `mediaKinds` yields no media rows — for free.
const mediaSpec = (kind: MediaKind): Omit<SlashSpec, "intent"> => {
    switch (kind) {
        case "image":
            return {
                label: msg`Image`,
                hint: msg`A picture with alt text`,
                icon: "image",
                keywords: ["picture", "photo", "img"],
            };
        case "video":
            return {
                label: msg`Video`,
                hint: msg`A video clip with captions`,
                icon: "video",
                keywords: ["clip", "movie"],
            };
        case "audio":
            return {
                label: msg`Audio`,
                hint: msg`A sound clip with a transcript`,
                icon: "audio",
                keywords: ["sound", "voice"],
            };
        case "embed":
            return {
                label: msg`Embed`,
                hint: msg`An external interactive frame`,
                icon: "embed",
                keywords: ["iframe", "external", "widget"],
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// Project the Shape into ordered specs — blocks first, then media, preserving
// each allow-list's own order (exactly Palette's grouping, flattened for a
// single keyboard list). Pure: Shape in, specs out, no i18n, no state.
const slashSpecs = (shape: EditorShape): readonly SlashSpec[] => [
    ...shape.rootKinds.map(
        (kind): SlashSpec => ({
            intent: { type: "block", kind },
            ...blockSpec(kind),
        }),
    ),
    ...shape.mediaKinds.map(
        (kind): SlashSpec => ({
            intent: { type: "media", kind },
            ...mediaSpec(kind),
        }),
    ),
];

// Localise the specs at the impure edge (the component has `useLingui`), turning
// descriptors into the strings the menu renders and filters on.
export const resolveSlashItems = (
    shape: EditorShape,
    i18n: I18n,
): readonly SlashItem[] =>
    slashSpecs(shape).map((spec) => ({
        intent: spec.intent,
        label: i18n._(spec.label),
        hint: i18n._(spec.hint),
        icon: spec.icon,
        keywords: spec.keywords,
    }));

// Filter by the text typed after `/`. Empty query ⇒ the whole (Shape-narrowed)
// list. Otherwise keep rows whose label or a keyword contains the query — pure,
// case-insensitive, order-preserving. This is the "type to narrow" channel.
export const matchSlash = (
    items: readonly SlashItem[],
    query: string,
): readonly SlashItem[] => {
    const q = query.trim().toLowerCase();
    if (q === "") return items;
    return items.filter(
        (item) =>
            item.label.toLowerCase().includes(q) ||
            item.keywords.some((k) => k.includes(q)),
    );
};
